import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

import { StudioApp } from '@/StudioApp';
import { AUTH_CONFIGURED, AuthProvider, useAuthSession } from '@/auth';
import { authErrorCode, describeAuthError } from '@/auth/authErrorMessage';
import { readInvitationParams } from '@/auth/invitation';
import { RapidSOSMark } from '@/components/RapidSOSMark/RapidSOSMark';
import { useAuth } from '@/hooks/useAuth';
import { LEGAL_DOCUMENTS } from '@/legal/documents';
import { LegalDocumentPage } from '@/legal/LegalDocumentPage';
import { AmplitudeBootstrap } from '@/lib/analytics';
import { rememberStudioSignIn } from '@/lib/studioIdentity';
import { AcceptInvitePage } from '@/pages/AcceptInvitePage';
import { AppSignInPage } from '@/pages/AppSignInPage/AppSignInPage';
import { SignInPage } from '@/pages/SignInPage';

function FullScreenSpinner({ label }: { label: string }) {
  return (
    <div className="w-full h-full flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4" role="status">
        <RapidSOSMark className="h-10 w-10" animation="route" />
        <p className="text-sm text-muted-foreground">{label}</p>
      </div>
    </div>
  );
}

/**
 * Unguarded shell for local dev runs that have no auth provider config. The
 * api client simply has no token provider, so requests go out
 * anonymously against a local backend.
 */
function AppShell() {
  // Wires the api client's access-token provider before any page mounts.
  useAuth();
  return (
    <>
      <AmplitudeBootstrap />
      <StudioApp />
    </>
  );
}

/**
 * Standalone auth gate. RapidSOS Studio is its own IdP-gated app (no
 * dependency on app-hub for login). Unauthenticated visitors see the
 * Studio sign-in screen and start the IdP flow from there, instead of
 * being bounced immediately to the hosted login.
 */
function AuthenticatedAppShell() {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading, user, logout } = useAuth();
  const { error } = useAuthSession();
  const { pathname } = useLocation();
  // Read once: the invitation token is only on the entry URL, and the SDK
  // rewrites the address bar as soon as it processes the callback.
  const invitation = useMemo(() => readInvitationParams(window.location.search), []);

  useEffect(() => {
    if (isAuthenticated) rememberStudioSignIn(user);
  }, [isAuthenticated, user]);

  // Always mounted so identify/reset run across login and logout.
  const analytics = <AmplitudeBootstrap />;

  // Ahead of the session checks below: an invitee arrives with no session (or a
  // session in some other org), and the token is what decides where they land.
  if (invitation) {
    return (
      <>
        {analytics}
        <AcceptInvitePage invitation={invitation} />
      </>
    );
  }

  if (error) {
    // Studio copy, never the provider's sentence: an IdP can answer an
    // org-gated refusal with the user's id and the `org_…` id, neither of
    // which Studio shows anyone.
    const notice = describeAuthError(error, t);
    console.warn('[Studio] Sign-in failed:', notice.kind, authErrorCode(error));
    return (
      <>
        {analytics}
        <div className="w-full h-full flex items-center justify-center bg-background">
          <div className="text-center max-w-md px-6">
            <h1 className="text-xl font-semibold mb-2">{notice.title}</h1>
            <p className="text-sm text-muted-foreground mb-4">{notice.body}</p>
            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="px-4 py-2 rounded bg-primary text-primary-foreground text-sm"
              >
                {t('common.retry')}
              </button>
              {notice.canSwitchAccount && (
                <button
                  type="button"
                  onClick={() => {
                    void logout({ logoutParams: { returnTo: window.location.origin } });
                  }}
                  className="px-4 py-2 rounded border border-border text-sm text-muted-foreground"
                >
                  {t('auth.useDifferentAccount')}
                </button>
              )}
            </div>
          </div>
        </div>
      </>
    );
  }

  // A published app or the apps portal signing its visitor in: anyone in an organization,
  // with or without a Studio seat, so ahead of the Studio access gate.
  if (pathname === '/app-signin') {
    return (
      <>
        {analytics}
        <AppSignInPage />
      </>
    );
  }

  if (isLoading) {
    return (
      <>
        {analytics}
        <FullScreenSpinner label={t('common.loading')} />
      </>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        {analytics}
        <SignInPage />
      </>
    );
  }

  return (
    <>
      {analytics}
      <StudioApp />
    </>
  );
}

function sanitizeReturnTo(returnTo: string | undefined): string {
  if (!returnTo) return '/';
  try {
    const url = returnTo.startsWith('/')
      ? new URL(returnTo, window.location.origin)
      : new URL(returnTo);
    if (url.origin !== window.location.origin) return '/';
    url.searchParams.delete('code');
    url.searchParams.delete('state');
    return `${url.pathname}${url.search}${url.hash}` || '/';
  } catch {
    return '/';
  }
}

/**
 * The auth provider must sit inside the router so the login callback updates
 * React Router instead of `history.replaceState` (which leaves `useLocation`
 * stuck on `?code=&state=` and breaks later navigations).
 */
function AuthProviderWithNavigate({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  return (
    <AuthProvider
      onRedirectCallback={(returnTo) => {
        navigate(sanitizeReturnTo(returnTo), { replace: true });
      }}
    >
      {children}
    </AuthProvider>
  );
}

/** The Terms of Use and Privacy Policy are readable signed out, so they sit ahead of the sign-in gate. */
function WithPublicLegalPages({ children }: { children: React.ReactNode }) {
  return (
    <Routes>
      {LEGAL_DOCUMENTS.map((legalDocument) => (
        <Route
          key={legalDocument.id}
          path={legalDocument.path}
          element={<LegalDocumentPage legalDocument={legalDocument} />}
        />
      ))}
      <Route path="*" element={children} />
    </Routes>
  );
}

export default function App() {
  if (!AUTH_CONFIGURED) {
    return (
      <BrowserRouter>
        <WithPublicLegalPages>
          <AppShell />
        </WithPublicLegalPages>
      </BrowserRouter>
    );
  }
  return (
    <BrowserRouter>
      <AuthProviderWithNavigate>
        <WithPublicLegalPages>
          <AuthenticatedAppShell />
        </WithPublicLegalPages>
      </AuthProviderWithNavigate>
    </BrowserRouter>
  );
}
